import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle, ArrowRight, Download, Layers, RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { billRunsService, type BillRunsData } from '../services/billRunsService';
import { ntaService } from '../services/ntaService';
import { tariffOpportunities, type TariffOpportunities } from '../lib/nta';
import { siteService } from '../services/siteService';
import { NewBadge } from '../components/NewBadge';
import { BillRunsMetrics } from '../components/billRunsPage/BillRunsMetrics';
import { MismatchDriversPanel } from '../components/billRunsPage/MismatchDriversPanel';
import { useBillReports } from '../hooks/useBillVerdicts';
import { buildBillRunRows } from '../lib/billRunRows';
import { NO_RUN_CONDITIONS, NO_RUN_SITE_FILTERS, type BillRunConditions } from '../lib/billRunConditions';
import { computeMetrics } from '../lib/billRunMetrics';
import { lastCompletedMonth } from '../lib/billRunsUrl';
import { monthLabel } from '../lib/billMonths';
import { clientMainReasons, driversFromRows } from '../lib/mismatchDrivers';
import {
  ENROLLED_SITE_STATUS, NEEDS_A_PERSON, billsReceivedSub, clientTotals, headline, reportsSub, percentOf, runsLink, sumClientTotals,
  usageFlow, validatedSub, type SiteCounts,
} from '../lib/dashboardMetrics';
import { dashboardFilename, dashboardSheets } from '../lib/dashboardExport';
import { DEFAULT_MATCH_TOLERANCE_PCT, MATCH_TOLERANCE_OPTIONS, matchTolerance } from '../lib/reportFormulas';
import { exportMultiSheetXLSX } from '../utils/exportUtils';
import type { BvBill } from '../types/billTypes';

// Dashboard (docs/superpowers/plans/2026-10-27-bv-dashboard-rework.md): one
// month at a glance. Built from the same rows and metrics as Bill Runs, and
// every figure opens Bill Runs filtered to the bills behind it — this page
// reads the state of the service, Bill Runs is where the bills are worked.
// Pipeline health (usage data flow, ingest, run history) now lives on Admin → Jobs.
// The month is Period end mode: a bill counts once, in the month it ends.

const fmtPct = (n: number | null) => (n == null ? '—' : `${n}%`);
const fmtDollars = (n: number) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

/** A figure; with `to`, it opens the bills (or sites) behind it. */
const Tile = ({ to, label, value, sub, tone, title, isNew }: { to?: string; label: string; value: string; sub?: string; tone?: string; title?: string; isNew?: boolean }) => {
  const body = (
    <>
      <p className="flex items-center gap-1 text-xs text-gray-500">{label}{isNew && <NewBadge />}</p>
      <p className={cn('mt-1 text-2xl font-semibold text-gray-900', tone)}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
    </>
  );
  const box = 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm';
  return to
    ? <Link to={to} title={title} className={cn('group', box, 'hover:border-blue-300 hover:bg-blue-50/40')}>{body}</Link>
    : <div title={title} className={box}>{body}</div>;
};

// The month's fetch, kept across visits so coming back to the Dashboard is
// instant. Refresh re-fetches. ponytail: per tab, until a full page reload;
// no expiry, add a max age if stale figures become a problem.
const monthCache = new Map<string, BillRunsData>();
// Another user signing in on this tab must not see the last user's bills.
supabase.auth.onAuthStateChange((event) => { if (event === 'SIGNED_OUT') monthCache.clear(); });

const SectionTitle = ({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) => (
  <div className="flex items-baseline justify-between">
    <h2 className="text-sm font-semibold text-gray-800">{children}</h2>
    {action}
  </div>
);

export function BillValidationDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const month = params.get('month') || lastCompletedMonth();
  const tolerance = matchTolerance(params.get('tol') ?? DEFAULT_MATCH_TOLERANCE_PCT);
  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  // Every link into Bill Runs carries the month and the tolerance, so it shows the same verdicts.
  const link = (patch: Parameters<typeof runsLink>[1] = {}) => runsLink(month, { tolerance, ...patch });

  // ── The month's bills: the Bill Runs fetch for one month ──────────────────
  const [data, setData] = useState<BillRunsData | null>(() => monthCache.get(month) ?? null);
  const [loading, setLoading] = useState(() => !monthCache.has(month));
  const [error, setError] = useState<string | null>(null);

  /** The month's bills, from the cache unless `force` (Refresh). */
  const load = useCallback(async (force = false) => {
    setError(null);
    const cached = force ? undefined : monthCache.get(month);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await billRunsService.fetch({
      months: [month], mode: 'end', siteFilters: NO_RUN_SITE_FILTERS, includeArchived: false,
    });
    // A partial fetch is shown but not cached, so the next visit tries again.
    if (res.success && res.data && res.data.partial.length === 0) monthCache.set(month, res.data);
    if (res.success) setData(res.data ?? null);
    else setError(res.error || 'Failed to load bills');
    setLoading(false);
  }, [month]);
  useEffect(() => { void load(); }, [load]);

  const bills = useMemo(() => data?.bills ?? [], [data]);
  const reportFor = useBillReports(bills, tolerance);
  const verdictFor = useCallback((bill: BvBill) => reportFor(bill)?.verdict ?? null, [reportFor]);
  const rows = useMemo(() => (data ? buildBillRunRows({
    bills: data.bills, emails: data.emails, queue: data.queue, analyses: data.analyses,
    sendHistory: data.sendHistory, sites: data.sites, tags: data.tags, lastBillTo: data.lastBillTo,
  }, 'end', [month], verdictFor) : []), [data, month, verdictFor]);
  const metrics = useMemo(() => computeMetrics(rows, NO_RUN_CONDITIONS), [rows]);
  const head = headline(metrics);
  const drivers = useMemo(() => driversFromRows(rows, reportFor), [rows, reportFor]);
  const flow = useMemo(() => usageFlow(rows), [rows]);
  const clients = useMemo(() => clientTotals(rows), [rows]);
  const reasons = useMemo(() => clientMainReasons(rows, reportFor), [rows, reportFor]);
  const clientsTotal = useMemo(() => sumClientTotals(clients), [clients]);

  // A metric card or driver opens Bill Runs with that filter instead of filtering here.
  const openRuns = (conditions: Partial<BillRunConditions>, retailer?: string[]) =>
    navigate(link({ conditions, ...(retailer ? { site: { ...NO_RUN_SITE_FILTERS, retailer } } : {}) }));

  // ── Sites onboarded and enrolled: the whole org, not the month ───────────
  // bv_sites_list (the Sites page's RPC) with one row a page: its total is the
  // count, so these match the Sites page's own count and its Status filter.
  const [siteCounts, setSiteCounts] = useState<SiteCounts | null>(null);
  const [siteCountsError, setSiteCountsError] = useState<string | null>(null);
  const loadSiteCounts = useCallback(async () => {
    const [all, enrolled] = await Promise.all([
      siteService.list({ size: 1 }),
      siteService.list({ status: ENROLLED_SITE_STATUS, size: 1 }),
    ]);
    if (all.success && enrolled.success && all.data && enrolled.data) {
      setSiteCounts({ onboarded: all.data.total, enrolled: enrolled.data.total });
      setSiteCountsError(null);
    } else {
      setSiteCountsError(all.error || enrolled.error || 'Failed to load sites');
    }
  }, []);
  useEffect(() => { void loadSiteCounts(); }, [loadSiteCounts]);

  const exportReport = () => exportMultiSheetXLSX(dashboardSheets({
    month, tolerance, head, sites: siteCounts, flow, progress: metrics.progress, clients, drivers,
  }), dashboardFilename(month));

  // NTA: sites with a cheaper network tariff found, by each site's latest result (not per month).
  const [nta, setNta] = useState<TariffOpportunities | null>(null);
  useEffect(() => {
    let mounted = true;
    void ntaService.listResultStatuses().then((r) => { if (mounted && r.success) setNta(tariffOpportunities(r.data ?? [])); });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {monthLabel(month)} at a glance — bills ending in the month. Click any figure to open the bills behind it.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs text-gray-600">
            Month
            <input
              type="month"
              data-testid="dashboard-month"
              className="mt-1 block h-9 rounded-md border border-gray-200 px-2 text-sm"
              value={month}
              onChange={(e) => e.target.value && setParam('month', e.target.value)}
            />
          </label>
          <label className="text-xs text-gray-600" title="A bill within this % of the expected invoice (or $0.50) is Matched. 5% is the floor.">
            Match within ±
            <select data-testid="dashboard-tolerance" value={tolerance}
              onChange={(e) => { const t = matchTolerance(e.target.value); setParam('tol', t === DEFAULT_MATCH_TOLERANCE_PCT ? null : String(t)); }}
              className="mt-1 block h-9 rounded-md border border-gray-200 bg-white px-2 text-sm">
              {MATCH_TOLERANCE_OPTIONS.map((t) => <option key={t} value={t}>{t}%</option>)}
            </select>
          </label>
          <Button variant="outline" className="gap-2" onClick={exportReport} disabled={!data}
            title="Summary, By client, Needs a person and Mismatch drivers, for this month and tolerance">
            <Download className="h-4 w-4" /> Export XLSX <NewBadge />
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => { void load(true); void loadSiteCounts(); }} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /> Refresh
          </Button>
          <Button className="gap-2" onClick={() => navigate(link())}>
            <Layers className="h-4 w-4" /> Open in Bill Runs
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {error}
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => void load(true)}>Retry</Button>
        </div>
      )}

      {data && data.partial.length > 0 && (
        <div data-testid="dashboard-partial" className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Some details could not be loaded, so figures that depend on them may be low: {data.partial.join('; ')}.</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => void load(true)}>Retry</Button>
        </div>
      )}

      {loading && !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : data && (
        <>
          {/* 1. The month at a glance */}
          <div data-testid="dashboard-tiles" className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
            {/* Sites: the whole org today, not the month. */}
            <Tile
              to="/bill-validation/sites"
              label="Onboarded"
              isNew
              value={siteCounts ? String(siteCounts.onboarded) : '—'}
              sub={siteCountsError ? 'Could not load sites' : 'BV sites, all statuses'}
              tone={siteCountsError ? 'text-gray-400' : undefined}
              title={siteCountsError ?? 'Every Bill Validation site in the system'}
            />
            {/* No link: the Sites page keeps its Status filter out of the URL. */}
            <Tile
              label="Enrolled"
              isNew
              value={siteCounts ? String(siteCounts.enrolled) : '—'}
              sub={siteCounts ? `${fmtPct(percentOf(siteCounts.enrolled, siteCounts.onboarded))} of onboarded` : `Status ${ENROLLED_SITE_STATUS}`}
              title={`BV sites with status ${ENROLLED_SITE_STATUS}. On the Sites page, filter Status to ${ENROLLED_SITE_STATUS}.`}
            />
            <Tile
              to={link({ tab: 'missing' })}
              label="Bills received"
              value={`${head.hasBill} / ${head.expected}`}
              sub={billsReceivedSub(head)}
              tone={head.missing ? undefined : 'text-emerald-600'}
            />
            <Tile
              to={link({ conditions: { status: ['matched', 'mismatched'] } })}
              label="Validated"
              value={String(head.validated)}
              sub={validatedSub(head)}
              tone={head.awaiting ? 'text-amber-600' : undefined}
            />
            <Tile
              to={link({ conditions: { status: ['matched'] } })}
              label="Matched"
              value={String(head.matched)}
              sub={`of ${head.validated} validated`}
              tone="text-emerald-600"
            />
            <Tile
              to={link({ conditions: { status: ['mismatched'] } })}
              label="Mismatched"
              value={String(head.mismatched)}
              sub={`${head.overcharged} over (${fmtDollars(clientsTotal.overchargedDollars)}) · ${head.undercharged} under (${fmtDollars(clientsTotal.underchargedDollars)})`}
              tone={head.mismatched ? 'text-red-600' : undefined}
            />
            <Tile
              to={link({ tab: 'reports' })}
              label="Reports sent"
              value={`${head.sent} / ${head.bills}`}
              sub={reportsSub(head)}
              tone={head.sendFailed ? 'text-red-600' : undefined}
            />
            <Tile
              to="/bill-validation/nta"
              label="Tariff opportunities"
              value={nta ? String(nta.sites) : '—'}
              sub={!nta ? 'Not available' : nta.assessed ? `sites · ${nta.approved} approved · ${nta.issued} issued` : 'No sites assessed yet'}
              tone={nta?.sites ? 'text-emerald-600' : undefined}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* 2. Where the month's bills are */}
            <Card className="border-0 shadow-sm lg:col-span-2">
              <CardContent className="pt-5 space-y-3">
                <SectionTitle>Where bills are</SectionTitle>
                <BillRunsMetrics
                  show={['progress', 'outcome']}
                  metrics={metrics}
                  conditions={NO_RUN_CONDITIONS}
                  onConditions={(patch) => openRuns(patch)}
                />
              </CardContent>
            </Card>

            {/* 3. What waits on a person */}
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-5 space-y-2">
                <SectionTitle>Needs a person</SectionTitle>
                <ul data-testid="dashboard-queue" className="divide-y divide-gray-100">
                  {NEEDS_A_PERSON.map(({ status, label, hint }) => {
                    const n = metrics.progress[status];
                    return (
                      <li key={status}>
                        <Link to={link({ conditions: { workflow: [status] } })}
                          className="flex items-center gap-3 py-2 hover:bg-gray-50">
                          <span className={cn('w-10 text-right text-lg font-semibold tabular-nums', n ? 'text-gray-900' : 'text-gray-300')}>{n}</span>
                          <span className="flex-1">
                            <span className="block text-sm text-gray-800">{label}</span>
                            <span className="block text-xs text-gray-400">{hint}</span>
                          </span>
                          <ArrowRight className="h-3.5 w-3.5 text-gray-300" />
                        </Link>
                      </li>
                    );
                  })}
                  <li>
                    <Link to={link({ tab: 'reports', conditions: { send: ['send_failed'] } })}
                      className="flex items-center gap-3 py-2 hover:bg-gray-50">
                      <span className={cn('w-10 text-right text-lg font-semibold tabular-nums', head.sendFailed ? 'text-red-600' : 'text-gray-300')}>{head.sendFailed}</span>
                      <span className="flex-1 text-sm text-gray-800">Send failed</span>
                      <ArrowRight className="h-3.5 w-3.5 text-gray-300" />
                    </Link>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* 4. Over/under by client */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 space-y-2">
              <SectionTitle>Over/under by client <NewBadge className="ml-1" /></SectionTitle>
              {clients.length === 0 ? (
                <p className="py-2 text-sm text-gray-500">No bills ending in {monthLabel(month)}.</p>
              ) : (
                <div className="max-h-96 overflow-auto">
                  <table data-testid="dashboard-clients" className="w-full text-sm">
                    <thead className="sticky top-0 bg-white text-xs text-gray-500">
                      <tr className="border-b border-gray-100">
                        <th className="py-2 pr-3 text-left font-medium">Client</th>
                        <th className="py-2 px-2 text-right font-medium">Bills</th>
                        <th className="py-2 px-2 text-right font-medium">Validated</th>
                        <th className="py-2 px-2 text-right font-medium">Overcharged</th>
                        <th className="py-2 px-2 text-right font-medium">Undercharged</th>
                        <th className="py-2 px-2 text-left font-medium" title="Most common reason among the client's over/undercharged bills: the section with the largest difference">Main reason</th>
                        <th className="py-2 pl-2 text-right font-medium">Reports sent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {clients.map((c) => {
                        const cells = (
                          <>
                            <td className="py-1.5 pr-3 text-gray-800">{c.entity || <span className="text-gray-400">(No client)</span>}</td>
                            <td className="py-1.5 px-2 text-right tabular-nums">{c.bills}</td>
                            <td className="py-1.5 px-2 text-right tabular-nums">{c.validated}</td>
                            <td className={cn('py-1.5 px-2 text-right tabular-nums', c.overcharged ? 'text-red-600' : 'text-gray-300')}>
                              {c.overcharged ? `${c.overcharged} · ${fmtDollars(c.overchargedDollars)}` : '0'}
                            </td>
                            <td className={cn('py-1.5 px-2 text-right tabular-nums', c.undercharged ? 'text-amber-700' : 'text-gray-300')}>
                              {c.undercharged ? `${c.undercharged} · ${fmtDollars(c.underchargedDollars)}` : '0'}
                            </td>
                            <td className="py-1.5 px-2 text-xs text-gray-600">
                              {reasons.get(c.entity) ? `${reasons.get(c.entity)!.reason} (${reasons.get(c.entity)!.bills})` : ''}
                            </td>
                            <td className="py-1.5 pl-2 text-right tabular-nums">{c.sent}</td>
                          </>
                        );
                        // A client row opens Bill Runs on that client's bills; with no client there is nothing to filter on.
                        return c.entity ? (
                          <tr key={c.entity} className="cursor-pointer hover:bg-gray-50"
                            onClick={() => navigate(link({ site: { ...NO_RUN_SITE_FILTERS, entity: c.entity } }))}>
                            {cells}
                          </tr>
                        ) : <tr key="(none)">{cells}</tr>;
                      })}
                    </tbody>
                    <tfoot className="sticky bottom-0 bg-white">
                      <tr className="border-t border-gray-200 font-semibold">
                        <td className="py-2 pr-3">Total</td>
                        <td className="py-2 px-2 text-right tabular-nums">{clientsTotal.bills}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{clientsTotal.validated}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{clientsTotal.overcharged} · {fmtDollars(clientsTotal.overchargedDollars)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{clientsTotal.undercharged} · {fmtDollars(clientsTotal.underchargedDollars)}</td>
                        <td />
                        <td className="py-2 pl-2 text-right tabular-nums">{clientsTotal.sent}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 5. Why bills mismatch */}
          <MismatchDriversPanel
            drivers={drivers}
            activeDriver={null}
            activeRetailers={[]}
            onDriver={(driver) => driver && openRuns({ driver })}
            onRetailer={(r) => openRuns({}, [r])}
          />
        </>
      )}

      {/* 5. Pipeline health lives on the Jobs page */}
      <p className="text-xs text-gray-500">
        <Link to="/bill-validation/admin/jobs" data-testid="dashboard-jobs-link" className="hover:underline">Usage data flow → Jobs</Link>
      </p>
    </div>
  );
}
